import { Check, ExternalLink, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type { AiInsight } from '@/features/ai-insights/types'

interface InsightCardProps {
  insight: AiInsight
  onAccept: (id: string) => void
  onSkip: (id: string) => void
  onOpenInGraph?: (insight: AiInsight) => void
  pinned?: boolean
  className?: string
}

export function InsightCard({
  insight,
  onAccept,
  onSkip,
  onOpenInGraph,
  pinned,
  className,
}: InsightCardProps) {
  const { t } = useTranslation()
  return (
    <Card asChild size="compact" tone={pinned ? 'primary' : 'neutral'} className={className}>
      <article
        data-slot="ai-insight-card"
        data-pinned={pinned ? 'true' : undefined}
        className="gap-3 p-3 text-left transition-colors hover:border-primary/40"
      >
        <header className="flex items-start gap-2">
          <h3 className="min-w-0 flex-1 break-words text-sm font-semibold leading-snug text-foreground">
            {insight.headline}
          </h3>
          <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-3xs">
            {Math.round(insight.relevance * 100)}
          </Badge>
        </header>

        <p className="break-words text-xs leading-relaxed text-muted-foreground">
          {insight.summary}
        </p>

        {insight.entities.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {insight.entities.map(e => (
              <Badge
                key={e.neo4j_id}
                variant="secondary"
                className="max-w-full px-1.5 py-0.5 text-3xs"
              >
                <span className="truncate">{e.name}</span>
              </Badge>
            ))}
          </div>
        )}

        {insight.relationships.length > 0 && (
          <ul className="space-y-1 border-l-2 border-border pl-2">
            {insight.relationships.map((r, i) => (
              <li
                key={`${r.edge_ids[0] ?? i}-${i}`}
                className="break-words text-2xs leading-snug text-foreground/80"
              >
                {r.phrasing}
              </li>
            ))}
          </ul>
        )}

        <footer className="flex items-center justify-between gap-2 pt-1">
          <Button
            size="sm"
            variant="solid"
            onClick={() => onAccept(insight.id)}
            className="h-7 text-2xs max-md:h-9"
          >
            <Check className="size-3" />
            {t('investigations.graph.aiInsights.addToInvestigation')}
          </Button>
          <div className="flex items-center gap-1">
            {onOpenInGraph && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOpenInGraph(insight)}
                className="size-7 p-0 max-md:size-9"
                aria-label={t('investigations.graph.aiInsights.openInGraph')}
              >
                <ExternalLink className="size-3.5" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onSkip(insight.id)}
              className="size-7 p-0 max-md:size-9"
              aria-label={t('investigations.graph.aiInsights.skip')}
            >
              <X className="size-3.5" />
            </Button>
          </div>
        </footer>
      </article>
    </Card>
  )
}
